import type {
  ReviewGuidanceBlockerCode,
  ReviewGuidanceBlockerView,
  ReviewGuidanceNextActionCode,
  ReviewGuidanceSeverity,
  ReviewGuidanceSummaryCode,
  ReviewGuidanceView,
} from "../api/types";

export const REVIEW_GUIDANCE_BLOCKER_CODES = [
  "DELIVERY_UNCERTAIN",
  "SENDING_IN_PROGRESS",
  "SUBMISSION_PENDING",
  "DECISION_SAFETY_UNAVAILABLE",
  "DECISION_SAFETY_BLOCKED",
  "EXTRACTION_IN_PROGRESS",
  "EXTRACTION_FAILED",
  "MISSING_DECISION",
  "HUMAN_REVIEW_REQUIRED",
  "DRAFT_GENERATION_IN_PROGRESS",
  "DRAFT_GENERATION_DISABLED",
  "MISSING_DRAFT",
  "STALE_DRAFT",
  "ATTACHMENT_STORAGE_UNAVAILABLE",
  "DRAFT_REJECTED",
  "DRAFT_APPROVAL_REQUIRED",
  "SENDING_DISABLED",
  "REQUEST_NOT_SENDABLE",
] as const satisfies readonly ReviewGuidanceBlockerCode[];

export const REVIEW_GUIDANCE_NEXT_ACTION_CODES = [
  "NONE",
  "WAIT",
  "CONTACT_ADMIN",
  "RETRY_ANALYSIS",
  "RECOMPUTE_DECISION",
  "REVIEW_EVIDENCE",
  "GENERATE_DRAFT",
  "REGENERATE_DRAFT",
  "REVIEW_DRAFT",
  "ENABLE_SENDING",
  "SEND_DRAFT",
  "CONTINUE_SUBMISSION",
  "RECONCILE_DELIVERY",
] as const satisfies readonly ReviewGuidanceNextActionCode[];

const SUMMARY_CODES = new Set<ReviewGuidanceSummaryCode>([
  ...REVIEW_GUIDANCE_BLOCKER_CODES,
  "READY_TO_SEND",
  "SUBMISSION_FAILED",
  "SENT",
  "CLOSED_REQUEST",
]);
const BLOCKER_CODES = new Set<ReviewGuidanceBlockerCode>(
  REVIEW_GUIDANCE_BLOCKER_CODES,
);
const NEXT_ACTION_CODES = new Set<ReviewGuidanceNextActionCode>(
  REVIEW_GUIDANCE_NEXT_ACTION_CODES,
);
const EXPECTED_SEVERITY: Readonly<
  Record<ReviewGuidanceBlockerCode, ReviewGuidanceSeverity>
> = {
  DELIVERY_UNCERTAIN: "danger",
  SENDING_IN_PROGRESS: "info",
  SUBMISSION_PENDING: "info",
  DECISION_SAFETY_UNAVAILABLE: "danger",
  DECISION_SAFETY_BLOCKED: "danger",
  MISSING_DECISION: "danger",
  EXTRACTION_FAILED: "danger",
  EXTRACTION_IN_PROGRESS: "info",
  HUMAN_REVIEW_REQUIRED: "warning",
  DRAFT_GENERATION_IN_PROGRESS: "info",
  DRAFT_GENERATION_DISABLED: "danger",
  MISSING_DRAFT: "warning",
  STALE_DRAFT: "danger",
  ATTACHMENT_STORAGE_UNAVAILABLE: "danger",
  DRAFT_REJECTED: "warning",
  DRAFT_APPROVAL_REQUIRED: "warning",
  SENDING_DISABLED: "warning",
  REQUEST_NOT_SENDABLE: "warning",
};

const PRIORITY = new Map(
  REVIEW_GUIDANCE_BLOCKER_CODES.map((code, index) => [code, index]),
);
const EXPECTED_ACTION: Readonly<
  Record<ReviewGuidanceBlockerCode, readonly ReviewGuidanceNextActionCode[]>
> = {
  DELIVERY_UNCERTAIN: ["RECONCILE_DELIVERY"],
  SENDING_IN_PROGRESS: ["WAIT"],
  SUBMISSION_PENDING: ["CONTINUE_SUBMISSION"],
  REQUEST_NOT_SENDABLE: ["CONTACT_ADMIN"],
  DECISION_SAFETY_UNAVAILABLE: ["CONTACT_ADMIN"],
  DECISION_SAFETY_BLOCKED: ["CONTACT_ADMIN", "RECOMPUTE_DECISION"],
  MISSING_DECISION: ["RECOMPUTE_DECISION"],
  EXTRACTION_FAILED: ["CONTACT_ADMIN", "RETRY_ANALYSIS"],
  EXTRACTION_IN_PROGRESS: ["WAIT"],
  HUMAN_REVIEW_REQUIRED: ["REVIEW_EVIDENCE", "GENERATE_DRAFT", "REGENERATE_DRAFT", "REVIEW_DRAFT", "WAIT"],
  DRAFT_GENERATION_IN_PROGRESS: ["WAIT"],
  DRAFT_GENERATION_DISABLED: ["CONTACT_ADMIN"],
  MISSING_DRAFT: ["GENERATE_DRAFT"],
  STALE_DRAFT: ["REGENERATE_DRAFT"],
  ATTACHMENT_STORAGE_UNAVAILABLE: ["CONTACT_ADMIN"],
  DRAFT_REJECTED: ["REGENERATE_DRAFT"],
  DRAFT_APPROVAL_REQUIRED: ["REVIEW_DRAFT"],
  SENDING_DISABLED: ["ENABLE_SENDING"],
};

export type ReviewGuidanceModel = ReviewGuidanceView & {
  evidenceValid: boolean;
};

const FAIL_CLOSED: ReviewGuidanceModel = Object.freeze({
  evidenceValid: false,
  schema_version: 1,
  state: "blocked",
  summary_code: "DECISION_SAFETY_UNAVAILABLE",
  primary_blocker: Object.freeze({
    code: "DECISION_SAFETY_UNAVAILABLE",
    severity: "danger",
  }),
  additional_blockers: Object.freeze([]) as unknown as ReviewGuidanceBlockerView[],
  additional_blocker_count: 0,
  next_action: Object.freeze({ code: "CONTACT_ADMIN" }),
});

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function exactKeys(value: Record<string, unknown>, expected: readonly string[]): boolean {
  const actual = Object.keys(value).sort();
  const sortedExpected = [...expected].sort();
  return actual.length === sortedExpected.length &&
    actual.every((key, index) => key === sortedExpected[index]);
}

function parseBlocker(value: unknown): ReviewGuidanceBlockerView | null {
  const item = record(value);
  if (!item || !exactKeys(item, ["code", "severity"])) return null;
  if (!BLOCKER_CODES.has(item.code as ReviewGuidanceBlockerCode)) return null;
  const code = item.code as ReviewGuidanceBlockerCode;
  if (item.severity !== EXPECTED_SEVERITY[code]) return null;
  return { code, severity: item.severity as ReviewGuidanceSeverity };
}

export function reviewGuidanceModel(value: unknown): ReviewGuidanceModel {
  const guidance = record(value);
  if (!guidance || !exactKeys(guidance, [
    "schema_version",
    "state",
    "summary_code",
    "primary_blocker",
    "additional_blockers",
    "additional_blocker_count",
    "next_action",
  ])) return FAIL_CLOSED;
  if (guidance.schema_version !== 1) return FAIL_CLOSED;
  if (!["blocked", "in_progress", "ready", "complete"].includes(String(guidance.state))) {
    return FAIL_CLOSED;
  }
  if (!SUMMARY_CODES.has(guidance.summary_code as ReviewGuidanceSummaryCode)) {
    return FAIL_CLOSED;
  }

  const primary = guidance.primary_blocker === null
    ? null
    : parseBlocker(guidance.primary_blocker);
  if (guidance.primary_blocker !== null && !primary) return FAIL_CLOSED;
  if (!Array.isArray(guidance.additional_blockers)) return FAIL_CLOSED;
  const additional = guidance.additional_blockers.map(parseBlocker);
  if (additional.some((item) => item === null)) return FAIL_CLOSED;
  if (
    !Number.isSafeInteger(guidance.additional_blocker_count) ||
    guidance.additional_blocker_count !== additional.length
  ) return FAIL_CLOSED;

  const action = record(guidance.next_action);
  if (
    !action ||
    !exactKeys(action, ["code"]) ||
    !NEXT_ACTION_CODES.has(action.code as ReviewGuidanceNextActionCode)
  ) return FAIL_CLOSED;

  const summaryCode = guidance.summary_code as ReviewGuidanceSummaryCode;
  const state = guidance.state as ReviewGuidanceView["state"];
  if (primary) {
    if (summaryCode !== primary.code || (state !== "blocked" && state !== "in_progress")) {
      return FAIL_CLOSED;
    }
  } else {
    const validNonBlocked =
      (state === "ready" && ["READY_TO_SEND", "SUBMISSION_FAILED"].includes(summaryCode) && action.code === "SEND_DRAFT") ||
      (state === "complete" && summaryCode === "SENT" && action.code === "NONE") ||
      (state === "complete" && summaryCode === "CLOSED_REQUEST" && action.code === "NONE");
    if (!validNonBlocked || additional.length > 0) return FAIL_CLOSED;
  }

  const codes = [primary?.code, ...additional.map((item) => item!.code)].filter(Boolean);
  if (new Set(codes).size !== codes.length) return FAIL_CLOSED;
  if (primary) {
    const blockerCodes = [primary.code, ...additional.map((item) => item!.code)];
    const priorities = blockerCodes.map((code) => PRIORITY.get(code));
    if (
      priorities.some((priority) => priority === undefined) ||
      priorities.some((priority, index) => index > 0 && priority! <= priorities[index - 1]!) ||
      !EXPECTED_ACTION[primary.code].includes(action.code as ReviewGuidanceNextActionCode)
    ) return FAIL_CLOSED;
    const expectedState = primary.code === "EXTRACTION_IN_PROGRESS" || primary.code === "SENDING_IN_PROGRESS" ||
      primary.code === "DRAFT_GENERATION_IN_PROGRESS"
      ? "in_progress"
      : "blocked";
    if (state !== expectedState) return FAIL_CLOSED;
  }

  return {
    evidenceValid: true,
    schema_version: 1,
    state,
    summary_code: summaryCode,
    primary_blocker: primary,
    additional_blockers: additional as ReviewGuidanceBlockerView[],
    additional_blocker_count: additional.length,
    next_action: { code: action.code as ReviewGuidanceNextActionCode },
  };
}

export function reviewGuidanceSummaryKey(
  guidance: Pick<ReviewGuidanceModel, "summary_code">,
): string {
  return `ws.guidance.summary.${guidance.summary_code}`;
}

export function reviewGuidanceNextActionKey(
  action: Pick<ReviewGuidanceView["next_action"], "code">,
): string {
  return `ws.guidance.action.${action.code}`;
}
